import React, { useContext, useEffect, useState } from "react";

import { MenuItem, OutlinedInput, SelectChangeEvent } from "@mui/material";
import { AddCircleOutline, RemoveCircleOutline } from "@mui/icons-material";
import { Grid } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { AiOutlineBulb } from "react-icons/ai";
import { useHistory } from "react-router-dom";
import { Status } from "Util/enums";
import { formatTimezone } from "Util/Format";
import * as yup from "yup";

import { AutoSuggest, Select } from "../../../../../../../../../components/Form";
import UserAddressComponent from "../../../../../../../../../components/Generic/SettingsUserForm/components/UserAddress";
import SkeletonComponent from "../../../../../../../../../components/Generic/Skeleton";
import { useAppState } from "../../../../../../../../../context/StateContext";
import useYupValidation from "../../../../../../../../../hooks/useYupValidation";
import { Branch } from "../../../../../../../../../models/branch";
import { Team } from "../../../../../../../../../models/team";
import { Timezone } from "../../../../../../../../../models/timezone";
import BranchService from "../../../../../../../../../services/branch.service";
import TeamsService from "../../../../../../../../../services/teams.service";
import TimezoneService from "../../../../../../../../../services/timezone.service";
import { BranchKeys, TeamKeys, TimezoneKeys } from "../../../../../../../../../util/queries";
import { MemberContext } from "../../context";
import { AccordionCard } from "../Accordion";
import useStyles, {
  BoxStyled,
  ChipStyled,
  InfoSection,
  InfoText,
  SelectBranchOption,
  TinyInfoText,
  Wrapper,
} from "./styles";

type Props = {
  errors?: yup.ValidationError[];
};

export default function Additional({ errors = [] }: Props) {
  const { t: translate } = useTranslation();
  const { location } = useHistory<{ isView: boolean }>();
  const { member, setMember, handleUpdateMember } = useContext(MemberContext);
  const { currentUser } = useAppState();
  const classes = useStyles();
  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  const [branches, setBranches] = useState<Branch[]>([]);

  const { data: timezoneList, isFetching: fetchingTimezoneList } = useQuery({
    queryKey: TimezoneKeys.getAll([]),
    queryFn: async () => {
      const response = await TimezoneService.getAll({
        sortBy: JSON.stringify(["gmtOffset"]),
      });

      if (response?.data && Array.isArray(response.data)) return response.data;

      return [];
    },
    onSuccess(data) {
      if (!data.length) return;

      setMember((state) => {
        if (state?.timezoneId) return state;

        const currentTimezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const timezoneFound = data.find((timezone) => timezone.name === currentTimezone);

        if (!timezoneFound) return state;

        return {
          ...state,
          timezone: timezoneFound,
          timezoneId: Number(timezoneFound?.id),
        };
      });
    },
    refetchOnWindowFocus: false,
  });

  const { data: branchList, isFetching: fetchingBranchList } = useQuery({
    queryKey: BranchKeys.getAll([]),
    queryFn: async () => {
      const response = await BranchService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
      });

      if (response?.data && Array.isArray(response.data)) return response.data;

      return [];
    },
    refetchOnWindowFocus: false,
  });

  const { data: teamsList, isFetching: fetchingTeamList } = useQuery({
    queryKey: TeamKeys.getAll([]),
    queryFn: async () => {
      const response = await TeamsService.getAll({
        isActive: 1,
        companyId: currentUser?.companyId,
      });

      if (response?.data && Array.isArray(response.data)) return response.data;

      return [];
    },
    refetchOnWindowFocus: false,
  });

  const renderChipValue = (selected: number[]) => {
    const selectedBranches: Branch[] = [];

    for (const branchId of selected) {
      const findBranch = branches.find((branch) => branch.id === branchId);

      if (findBranch) selectedBranches.push(findBranch);
    }

    return (
      <BoxStyled>
        {selectedBranches?.map((value: Branch, index: number) => {
          if (index <= 1) return <ChipStyled key={value.id} label={value.name} />;

          return null;
        })}
      </BoxStyled>
    );
  };

  const onSelectedBranch = (branchesId: number[]) => {
    if (branchList?.length && branchesId.includes(0)) {
      setBranches([...branchList]);
      setMember((state) => ({
        ...state,
        branches: branchList,
      }));
    } else if (branchesId.includes(-1)) {
      setBranches([]);
      setMember((state) => ({
        ...state,
        branches: [],
      }));
    } else {
      const branchArr: Branch[] = [];

      for (const branchId of branchesId) {
        const findBranch = branchList?.find((branch) => branch.id === Number(branchId));

        if (findBranch) branchArr.push(findBranch);
      }
      setBranches([...branchArr]);
      handleUpdateMember("branches", branchArr);
    }
  };

  useEffect(() => {
    if (member.branches?.length) {
      setBranches([...member.branches]);
    }
  }, [member, timezoneList]);

  const filteredTeams = teamsList?.filter((team) => {
    return member?.teams?.find((t) => t.id === team.id);
  });

  return (
    <Wrapper>
      <UserAddressComponent
        view={location?.state?.isView}
        userForm={member as any}
        isUsingCompanyAddress={
          currentUser?.company?.locationId
            ? member?.locationId === currentUser?.company?.locationId
            : false
        }
        setUsingCompanyAddress={(checked) => {
          if (checked) {
            setMember({
              ...member,
              locationId: currentUser?.company?.locationId,
              location: currentUser?.company?.location,
            });
          } else {
            setMember({
              ...member,
              locationId: undefined,
              location: undefined,
            });
          }
        }}
        updateOrCreate={({ location, locationId }) =>
          setMember({ ...(member || {}), location, locationId })
        }
        disabled={!currentUser?.company?.location?.longAddress}
      />
      <div>
        <AccordionCard.Divider className={classes.my32} />
        <div>
          <Grid
            container
            direction="row"
            alignItems={
              fetchingTeamList || fetchingBranchList || fetchingTimezoneList
                ? "flex-end"
                : "flex-start"
            }
            spacing={2}
          >
            <Grid item xs={12} md={member?.id ? 3 : 4} lg={3}>
              <SkeletonComponent isLoading={fetchingTimezoneList}>
                <>
                  <AutoSuggest
                    disabled={location?.state?.isView}
                    label={translate("timezone")}
                    value={member.timezone}
                    inputValue={member?.timezone?.name || ""}
                    options={timezoneList}
                    dataCy="timezone"
                    getOptionLabel={(timezone: Timezone) => {
                      return formatTimezone(timezone);
                    }}
                    getOptionSelected={(option: Timezone, value: Timezone) => {
                      return option && value && option.id === value.id;
                    }}
                    onChange={(value: Timezone) => {
                      setMember((state) => ({
                        ...state,
                        timezoneId: value?.id ?? null,
                        timezone: value,
                      }));
                    }}
                    placeholder={translate("select")}
                  />
                  <TinyInfoText>
                    {translate("timezoneIsUpdatedAutomaticallyToMatchYourComputerTimezone")}
                  </TinyInfoText>
                </>
              </SkeletonComponent>
            </Grid>

            <Grid item xs={12} md={3}>
              <SkeletonComponent isLoading={fetchingBranchList}>
                <Select
                  dataCy="selectBranch"
                  disabled={location?.state?.isView}
                  label={translate("branch")}
                  className={classes.select}
                  name="branchList"
                  value={branches?.length ? branches?.map((branch) => branch?.id || -2) : []}
                  error={fieldHasError("branches")}
                  errorMessage={getErrorByField("branches")}
                  onChange={(event: SelectChangeEvent<any>) => onSelectedBranch(event.target.value)}
                  input={<OutlinedInput id="select-multiple-chip" label="Chip" />}
                  multiple
                  required
                  renderValue={(selected) => renderChipValue(selected as number[])}
                  placeholder={translate("select")}
                >
                  {Number(branches?.length) < Number(branchList?.length) && (
                    <MenuItem value={0}>
                      <SelectBranchOption>
                        <AddCircleOutline className="mr-3" />
                        {translate("addAll")}
                      </SelectBranchOption>
                    </MenuItem>
                  )}
                  {branches?.length === branchList?.length && (
                    <MenuItem value={-1}>
                      <SelectBranchOption isRemoving>
                        <RemoveCircleOutline className="mr-3" />
                        {translate("removeAll")}
                      </SelectBranchOption>
                    </MenuItem>
                  )}
                  {branchList?.map((branch) => (
                    <MenuItem value={branch.id} key={branch.id}>
                      {branch.name}
                    </MenuItem>
                  ))}
                </Select>
              </SkeletonComponent>
            </Grid>

            <Grid item xs={12} md={3}>
              <SkeletonComponent isLoading={fetchingTeamList}>
                <AutoSuggest
                  disabled={location?.state?.isView}
                  label={translate("teams")}
                  value={filteredTeams || []}
                  multiple
                  options={teamsList}
                  getOptionLabel={(option: Team) => {
                    return option && option.name ? option.name : "";
                  }}
                  getOptionSelected={(option: Team, value: Team) => option?.id === value?.id}
                  onChange={(teams: Team[]) => {
                    handleUpdateMember("teams", teams);
                  }}
                  placeholder={translate("select")}
                />
              </SkeletonComponent>
            </Grid>

            {member?.id ? (
              <Grid item xs={12} md={3}>
                <InfoSection>
                  <AiOutlineBulb fontSize={42} color="#475467" />
                  <InfoText>
                    {translate("ifTheTeamAlreadyHasALeaderTheMemberWillBeAddedAsStaff")}
                  </InfoText>
                </InfoSection>
              </Grid>
            ) : null}
          </Grid>
        </div>
      </div>
    </Wrapper>
  );
}
